<div class="legal-page">
  <h1>Terms of Service</h1>
  <p class="last-updated">Last Updated: November 11, 2025</p>

  <section>
    <h2>Acceptance of Terms</h2>
    <p>
      By accessing and using this website and its applications ("Service"), you accept and agree to be bound by these Terms of Service.
      If you do not agree to these terms, please do not use the Service.
    </p>
  </section>

  <section>
    <h2>Description of Service</h2>
    <p>
      This website provides a collection of <strong>client-side web applications</strong> that run entirely in your browser.
      These tools include (but are not limited to):
    </p>
    <ul>
      <li>PDF tools (merge, fill & sign, convert)</li>
      <li>Image tools (resize, compress, EXIF stripper, face blur)</li>
      <li>Text tools (Base64, URL encode/decode, UUID generation)</li>
      <li>Data tools (CSV/JSON/YAML conversion, charts)</li>
      <li>Productivity tools (calculator, converters, checklists)</li>
      <li>Weather notifications (email alerts for weather preferences)</li>
      <li>And other utilities</li>
    </ul>
    <p>
      All file processing occurs <strong>locally in your browser</strong>. Your files are not uploaded to servers unless explicitly stated (e.g., weather notification email registration).
    </p>
  </section>

  <section>
    <h2>Age Restriction</h2>
    <p>
      You must be at least <strong>13 years old</strong> to use this Service.
      If you are under 13, you may only use the Service with the involvement and consent of a parent or guardian.
    </p>
    <p>
      If you are a parent or guardian and believe your child under 13 has provided personal information, please contact us to delete it.
    </p>
  </section>

  <section>
    <h2>Acceptable Use</h2>
    <p>
      You agree to use the Service only for lawful purposes. You will NOT:
    </p>
    <ul>
      <li>❌ Use the Service to process illegal content (pirated material, illegal pornography, etc.)</li>
      <li>❌ Attempt to hack, reverse-engineer, or compromise the Service</li>
      <li>❌ Use automated scripts or bots to abuse the Service</li>
      <li>❌ Distribute malware or malicious code through the Service</li>
      <li>❌ Violate any applicable laws or regulations</li>
      <li>❌ Infringe upon intellectual property rights of others</li>
    </ul>
    <p>
      We reserve the right to terminate or restrict access to the Service for violations of these terms.
    </p>
  </section>

  <section>
    <h2>Disclaimer of Warranties</h2>
    <p>
      <strong>THE SERVICE IS PROVIDED "AS IS" AND "AS AVAILABLE" WITHOUT WARRANTIES OF ANY KIND, EXPRESS OR IMPLIED.</strong>
    </p>
    <p>
      We make no guarantees that:
    </p>
    <ul>
      <li>The Service will be uninterrupted, secure, or error-free</li>
      <li>The results obtained from using the Service will be accurate or reliable</li>
      <li>Any defects or errors will be corrected</li>
      <li>The Service will meet your specific requirements</li>
    </ul>
    <p>
      <strong>Specific Disclaimers:</strong>
    </p>
    <ul>
      <li><strong>Not Legal Advice:</strong> The Fill & Sign tool is for convenience only. Digital signatures created with this tool are <strong>not cryptographic signatures</strong> and may not be legally binding. Consult a legal professional for important documents.</li>
      <li><strong>Not Financial Advice:</strong> Any financial calculations or conversions are for informational purposes only. Verify critical calculations independently.</li>
      <li><strong>Not Professional Services:</strong> This Service does not replace professional services (legal, medical, financial, etc.).</li>
    </ul>
  </section>

  <section>
    <h2>Limitation of Liability</h2>
    <p>
      <strong>TO THE MAXIMUM EXTENT PERMITTED BY LAW, WE SHALL NOT BE LIABLE FOR ANY INDIRECT, INCIDENTAL, SPECIAL, CONSEQUENTIAL, OR PUNITIVE DAMAGES ARISING OUT OF OR RELATING TO YOUR USE OF THE SERVICE.</strong>
    </p>
    <p>
      This includes (but is not limited to) damages for:
    </p>
    <ul>
      <li>Loss of data, files, or work product</li>
      <li>Loss of profits or business opportunities</li>
      <li>Service interruptions or errors</li>
      <li>Unauthorized access to your device or data</li>
    </ul>
    <p>
      <strong>Important:</strong> Because the Service processes files locally in your browser, we have no access to your files and cannot recover them if lost.
      Always keep backups of important files before processing them with any tool.
    </p>
  </section>

  <section>
    <h2>Data & Privacy</h2>
    <p>
      Your use of the Service is also governed by our <a href="#/privacy">Privacy Policy</a>. Key points:
    </p>
    <ul>
      <li><strong>Local processing:</strong> Files are processed in your browser and never uploaded to servers</li>
      <li><strong>localStorage:</strong> Some apps store preferences locally on your device</li>
      <li><strong>Email notifications:</strong> If you opt in, your email and preferences are stored on our servers (Cloudflare KV)</li>
      <li><strong>No tracking:</strong> We don't use cookies, analytics trackers, or ad networks</li>
    </ul>
    <p>
      See the full <a href="#/privacy">Privacy Policy</a> for details.
    </p>
  </section>

  <section>
    <h2>Third-Party Services</h2>
    <p>
      Some applications use third-party services:
    </p>
    <ul>
      <li><strong>Open-Meteo API:</strong> Weather data (free, open-source weather API)</li>
      <li><strong>GitHub API:</strong> For the GitHub Markdown Viewer (if you provide a Personal Access Token)</li>
      <li><strong>Cloudflare Pages:</strong> Website hosting</li>
      <li><strong>Cloudflare Workers:</strong> Weather notification emails (MailChannels)</li>
    </ul>
    <p>
      These third-party services have their own terms and privacy policies. We are not responsible for their practices.
    </p>
  </section>

  <section>
    <h2>Intellectual Property</h2>
    <p>
      The Service's source code, design, and functionality are protected by copyright and other intellectual property laws.
    </p>
    <ul>
      <li><strong>Open Source:</strong> If this project is open-source (check the GitHub repository), usage is governed by the project's license (MIT, Apache 2.0, etc.)</li>
      <li><strong>Third-Party Libraries:</strong> This Service uses open-source libraries (pdf-lib, marked, DOMPurify, etc.) which have their own licenses</li>
      <li><strong>User Content:</strong> You retain all rights to files and data you process with the Service. We do not claim ownership of your content.</li>
    </ul>
  </section>

  <section>
    <h2>User Responsibilities</h2>
    <p>
      You are responsible for:
    </p>
    <ul>
      <li><strong>Browser compatibility:</strong> Using a modern, supported browser (Chrome, Firefox, Safari, Edge)</li>
      <li><strong>Data backups:</strong> Keeping backups of important files before processing</li>
      <li><strong>Device security:</strong> Securing your device, especially if storing sensitive data in localStorage</li>
      <li><strong>Legal compliance:</strong> Ensuring your use of the Service complies with applicable laws</li>
    </ul>
  </section>

  <section>
    <h2>Modifications to Service</h2>
    <p>
      We reserve the right to:
    </p>
    <ul>
      <li>Modify, suspend, or discontinue any part of the Service at any time</li>
      <li>Update features, add new tools, or remove existing tools</li>
      <li>Change pricing (if we introduce paid features in the future)</li>
    </ul>
    <p>
      We will make reasonable efforts to notify users of significant changes, but we are not obligated to do so.
    </p>
  </section>

  <section>
    <h2>Modifications to Terms</h2>
    <p>
      We may update these Terms of Service from time to time. Changes will be posted on this page with an updated "Last Updated" date.
    </p>
    <p>
      <strong>Your continued use of the Service after changes constitutes acceptance of the updated terms.</strong>
      If you do not agree to the new terms, you must stop using the Service.
    </p>
  </section>

  <section>
    <h2>Termination</h2>
    <p>
      You may stop using the Service at any time. We may terminate or suspend your access to the Service for:
    </p>
    <ul>
      <li>Violations of these Terms of Service</li>
      <li>Abusive or illegal behavior</li>
      <li>Technical or security reasons</li>
    </ul>
    <p>
      Upon termination, you should clear all locally stored data and cease using the Service.
    </p>
  </section>

  <section>
    <h2>Indemnification</h2>
    <p>
      You agree to indemnify and hold harmless the Service operators, contributors, and affiliates from any claims, damages, losses, or expenses
      arising from:
    </p>
    <ul>
      <li>Your use of the Service</li>
      <li>Your violation of these Terms</li>
      <li>Your violation of any third-party rights</li>
      <li>Content you process or store using the Service</li>
    </ul>
  </section>

  <section>
    <h2>Governing Law</h2>
    <p>
      These Terms shall be governed by and construed in accordance with the laws of [Your Jurisdiction], without regard to its conflict of law provisions.
    </p>
    <p>
      Any disputes arising from these Terms or use of the Service shall be resolved in the courts of [Your Jurisdiction].
    </p>
  </section>

  <section>
    <h2>Severability</h2>
    <p>
      If any provision of these Terms is found to be invalid or unenforceable, the remaining provisions shall continue in full force and effect.
    </p>
  </section>

  <section>
    <h2>Entire Agreement</h2>
    <p>
      These Terms, together with the <a href="#/privacy">Privacy Policy</a> and <a href="#/accessibility">Accessibility Statement</a>,
      constitute the entire agreement between you and the Service regarding your use of the Service.
    </p>
  </section>

  <section>
    <h2>Contact</h2>
    <p>
      If you have questions about these Terms of Service, please contact us:
    </p>
    <ul>
      <li><a href="#/feedback">Send feedback through the website</a></li>
      <li>Email <a href="mailto:contact@spastudio.org">contact@spastudio.org</a></li>
    </ul>
  </section>
</div>

<style>
  .legal-page {
    max-width: 760px;
    margin: 0 auto;
    padding: 2rem 1.5rem 4rem;
    color: hsl(var(--theme-foreground));
  }
  h1 {
    font-size: 1.75rem;
    font-weight: 700;
    margin-bottom: 0.375rem;
    letter-spacing: -0.02em;
    color: hsl(var(--theme-foreground));
  }
  .last-updated {
    color: hsl(var(--theme-muted-foreground));
    font-size: 0.8125rem;
    margin-bottom: 2.5rem;
  }
  h2 {
    font-size: 1.25rem;
    font-weight: 600;
    margin-top: 2.5rem;
    margin-bottom: 0.75rem;
    color: hsl(var(--theme-foreground));
    border-bottom: 1px solid hsl(var(--theme-border) / 0.3);
    padding-bottom: 0.5rem;
    letter-spacing: -0.01em;
  }
  section { margin-bottom: 1.5rem; }
  p {
    font-size: 0.9375rem;
    line-height: 1.7;
    margin-bottom: 0.875rem;
    color: hsl(var(--theme-muted-foreground));
  }
  ul {
    margin: 0.75rem 0;
    padding-left: 1.25rem;
    line-height: 1.7;
  }
  li {
    margin-bottom: 0.375rem;
    font-size: 0.9375rem;
    color: hsl(var(--theme-muted-foreground));
  }
  strong { font-weight: 600; color: hsl(var(--theme-foreground)); }
  a {
    color: hsl(var(--theme-primary));
    text-decoration: none;
    border-bottom: 1px solid hsl(var(--theme-primary) / 0.3);
    transition: border-color 200ms ease;
  }
  a:hover { border-color: hsl(var(--theme-primary)); }
  @media (max-width: 640px) {
    .legal-page { padding: 1.5rem 1rem 3rem; }
    h1 { font-size: 1.5rem; }
    h2 { font-size: 1.125rem; }
  }
</style>
